import {
  DEFAULT_MAP_TEXT_LABEL_FONT_FAMILY,
  type MapTextLabel,
} from "@west-marches/shared";

import { computeTextBannerLayout, drawTextBanner } from "@/lib/drawTextBanner";
import { resolveTextLabelFontStack } from "@/lib/textLabelFonts";

const LINE_HEIGHT_FACTOR = 1;
const SELECTION_PADDING = 6;
const SELECTION_DASH = [5, 4];
const SHADOW_BLUR = 4;
const SHADOW_COLOR = "rgba(0, 0, 0, 0.7)";
const ROTATION_HANDLE_RADIUS = 12;
const ROTATION_HANDLE_HIT_RADIUS = 24;
const MAX_TEXT_LABEL_LINES = 2;

export function buildFontString(
  label: Pick<MapTextLabel, "fontSize" | "bold" | "italic" | "fontFamily">,
): string {
  const style = label.italic ? "italic " : "";
  const weight = label.bold ? "bold " : "";
  const fontFamily = resolveTextLabelFontStack(
    label.fontFamily ?? DEFAULT_MAP_TEXT_LABEL_FONT_FAMILY,
  );
  return `${style}${weight}${label.fontSize}px ${fontFamily}`;
}

export function measureTextLabel(
  ctx: CanvasRenderingContext2D,
  label: MapTextLabel,
): { width: number; height: number; lineHeight: number; lines: string[] } {
  ctx.font = buildFontString(label);
  const lines = label.text.split("\n").slice(0, MAX_TEXT_LABEL_LINES);
  const lineHeight = label.fontSize * LINE_HEIGHT_FACTOR;
  let maxWidth = 0;
  for (const line of lines) {
    const metrics = ctx.measureText(line || " ");
    maxWidth = Math.max(maxWidth, metrics.width);
  }
  return {
    width: maxWidth,
    height: lineHeight * lines.length,
    lineHeight,
    lines,
  };
}

/**
 * Returns the painted text height for `lines` rendered with the given
 * `fontString` at `fontSize`, using the same font-metric logic the canvas
 * draw path uses (fontBoundingBox metrics, matching `drawTextLabels`). This
 * keeps the live editor preview's banner sizing in sync with the rendered
 * banner so they don't visually drift (e.g. decorative fonts whose font
 * bounds are taller than the CSS line box, which would otherwise make the
 * editor banner look squished compared to the rendered version).
 */
export function measureTextLabelTextHeight(
  ctx: CanvasRenderingContext2D,
  fontString: string,
  fontSize: number,
  lines: string[],
): number {
  ctx.font = fontString;
  const sampleLine = lines.find((line) => line.trim().length > 0) ?? "Mg";
  const { ascent, descent } = getTextLineMetrics(ctx, sampleLine, fontSize);
  const glyphHeight = ascent + descent;
  const lineHeight = fontSize * LINE_HEIGHT_FACTOR;
  return glyphHeight + lineHeight * Math.max(0, lines.length - 1);
}

function getTextLineMetrics(
  ctx: CanvasRenderingContext2D,
  sampleText: string,
  fontSize: number,
  preference: "fontBounds" | "actualBounds" = "fontBounds",
): { ascent: number; descent: number } {
  const metrics = ctx.measureText(sampleText || "Mg");
  const ascent = preference === "actualBounds"
    ? metrics.actualBoundingBoxAscent ??
      metrics.fontBoundingBoxAscent ??
      fontSize * 0.8
    : metrics.fontBoundingBoxAscent ??
      metrics.actualBoundingBoxAscent ??
      fontSize * 0.8;
  const descent = preference === "actualBounds"
    ? metrics.actualBoundingBoxDescent ??
      metrics.fontBoundingBoxDescent ??
      fontSize * 0.2
    : metrics.fontBoundingBoxDescent ??
      metrics.actualBoundingBoxDescent ??
      fontSize * 0.2;
  return { ascent, descent };
}

export function getTextLabelBounds(
  ctx: CanvasRenderingContext2D,
  label: MapTextLabel,
): { x: number; y: number; width: number; height: number } {
  const { width, lineHeight, lines } = measureTextLabel(ctx, label);
  ctx.font = buildFontString(label);
  const { ascent, descent } = getTextLineMetrics(
    ctx,
    lines.find((line) => line.trim().length > 0) ?? "Mg",
    label.fontSize,
    "actualBounds",
  );
  const glyphHeight = ascent + descent;
  const textHeight = glyphHeight + lineHeight * Math.max(0, lines.length - 1);

  let boundsWidth = width;
  let boundsHeight = textHeight;

  if (label.banner) {
    const layout = computeTextBannerLayout({
      width,
      height: textHeight,
      fontSize: label.fontSize,
      lineCount: lines.length,
    });
    if (layout) {
      boundsWidth = Math.max(boundsWidth, layout.width);
      // Symmetric envelope around label centre that contains both the text
      // (centred) and the banner (shifted by verticalOffset). Keeping bounds
      // centred on label.x/label.y keeps the rotation handle and hit-test
      // math simple.
      const bannerHalfExtent = layout.height / 2 + Math.abs(layout.verticalOffset);
      boundsHeight = Math.max(boundsHeight, bannerHalfExtent * 2);
    }
  }

  return {
    x: label.x - boundsWidth / 2,
    y: label.y - boundsHeight / 2,
    width: boundsWidth,
    height: boundsHeight,
  };
}

export function drawTextLabels(
  ctx: CanvasRenderingContext2D,
  textLabels: Record<string, MapTextLabel>,
  editingLabelId: string | null,
  selectedLabelId: string | null,
): void {
  const labels = Object.values(textLabels);
  if (labels.length === 0) return;

  for (const label of labels) {
    if (label.id === editingLabelId) continue;

    const { width: textWidth, lines, lineHeight } = measureTextLabel(ctx, label);
    const rotation = label.rotation ?? 0;

    ctx.save();
    ctx.translate(label.x, label.y);
    if (rotation !== 0) ctx.rotate(rotation);

    ctx.font = buildFontString(label);
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const totalHeight = lineHeight * lines.length;
    const { ascent, descent } = getTextLineMetrics(
      ctx,
      lines.find((line) => line.trim().length > 0) ?? "Mg",
      label.fontSize,
    );
    const halfLeading = Math.max(0, (lineHeight - (ascent + descent)) / 2);
    const startY = -totalHeight / 2 + halfLeading + ascent;

    if (label.banner) {
      const glyphHeight = ascent + descent;
      const textHeight = glyphHeight + lineHeight * Math.max(0, lines.length - 1);
      drawTextBanner(ctx, {
        width: textWidth,
        height: textHeight,
        fontSize: label.fontSize,
        lineCount: lines.length,
      });
    }

    ctx.shadowColor = SHADOW_COLOR;
    ctx.shadowBlur = SHADOW_BLUR;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;
    ctx.fillStyle = label.color;
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], 0, startY + i * lineHeight);
    }

    if (selectedLabelId === label.id) {
      const bounds = getTextLabelBounds(ctx, label);
      const halfW = bounds.width / 2 + SELECTION_PADDING;
      const halfH = bounds.height / 2 + SELECTION_PADDING;
      ctx.setLineDash(SELECTION_DASH);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(-halfW, -halfH, halfW * 2, halfH * 2);

      ctx.beginPath();
      ctx.arc(halfW, halfH, ROTATION_HANDLE_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
      ctx.fill();
      ctx.setLineDash([]);
      ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    ctx.restore();
  }
}

export function getRotationHandleWorldPos(
  ctx: CanvasRenderingContext2D,
  label: MapTextLabel,
): { x: number; y: number } {
  const bounds = getTextLabelBounds(ctx, label);
  const localX = bounds.width / 2 + SELECTION_PADDING;
  const localY = bounds.height / 2 + SELECTION_PADDING;
  const rotation = label.rotation ?? 0;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  return {
    x: label.x + localX * cos - localY * sin,
    y: label.y + localX * sin + localY * cos,
  };
}

export function hitTestRotationHandle(
  ctx: CanvasRenderingContext2D,
  label: MapTextLabel,
  worldX: number,
  worldY: number,
): boolean {
  const handle = getRotationHandleWorldPos(ctx, label);
  const dx = worldX - handle.x;
  const dy = worldY - handle.y;
  return dx * dx + dy * dy <= ROTATION_HANDLE_HIT_RADIUS * ROTATION_HANDLE_HIT_RADIUS;
}

export function hitTestTextLabels(
  ctx: CanvasRenderingContext2D,
  textLabels: Record<string, MapTextLabel>,
  worldX: number,
  worldY: number,
): MapTextLabel | null {
  const labels = Object.values(textLabels);
  for (let i = labels.length - 1; i >= 0; i--) {
    const label = labels[i];
    const bounds = getTextLabelBounds(ctx, label);
    const halfW = bounds.width / 2 + SELECTION_PADDING;
    const halfH = bounds.height / 2 + SELECTION_PADDING;

    let localX = worldX - label.x;
    let localY = worldY - label.y;

    const rotation = label.rotation ?? 0;
    if (rotation !== 0) {
      const cos = Math.cos(-rotation);
      const sin = Math.sin(-rotation);
      const rx = localX * cos - localY * sin;
      const ry = localX * sin + localY * cos;
      localX = rx;
      localY = ry;
    }

    if (
      localX >= -halfW &&
      localX <= halfW &&
      localY >= -halfH &&
      localY <= halfH
    ) {
      return label;
    }
  }
  return null;
}
